import axios from 'axios';

// ──────────────────────────────────────────────────────────────
// Self-contained backend — uses relative URLs
// The Kubernetes ingress routes /api/* to port 8001 (local backend)
// No dependency on any external backend (tourney-hub-121 removed)
// ──────────────────────────────────────────────────────────────
const API_BASE   = '/api';
const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : '';

console.log('[BTCCP] Using self-contained backend at', BACKEND_URL, '| API:', API_BASE);

// Create axios instance with relative baseURL
const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Token management
export const setTokens = (access, refresh) => {
  if (access)  localStorage.setItem('btccp_access_token', access);
  else         localStorage.removeItem('btccp_access_token');
  if (refresh) localStorage.setItem('btccp_refresh_token', refresh);
  else         localStorage.removeItem('btccp_refresh_token');
};

export const getTokens = () => ({
  accessToken:  localStorage.getItem('btccp_access_token'),
  refreshToken: localStorage.getItem('btccp_refresh_token'),
});

export const clearTokens = () => {
  localStorage.removeItem('btccp_access_token');
  localStorage.removeItem('btccp_refresh_token');
};

// Request interceptor — add Bearer token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('btccp_access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handle 401 with token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else       prom.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch((err) => Promise.reject(err));
      }
      originalRequest._retry = true;
      isRefreshing = true;
      const currentRefreshToken = localStorage.getItem('btccp_refresh_token');
      if (!currentRefreshToken) {
        isRefreshing = false;
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }
      try {
        const response = await axios.post(`${API_BASE}/auth/refresh`, {
          refresh_token: currentRefreshToken,
        });
        const { access_token, refresh_token } = response.data.tokens || response.data;
        setTokens(access_token, refresh_token);
        processQueue(null, access_token);
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default api;
export { API_BASE, BACKEND_URL };
